import { PageHero } from '@components/common';
import { getSectorCard } from '@data/sectorCards';
import { CALENDLY_URL } from '@constants';
import { Link } from 'react-router-dom';
import { Shield, AlertTriangle, Lock, TrendingUp, Building2 } from 'lucide-react';
import PageFAQ from '@components/sections/PageFAQ';

export default function FinancePage() {
  const sectorSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Fractional CISO for Financial Services',
    description: 'Expert cybersecurity leadership for banks, fintechs, and financial institutions',
    url: 'https://thecisonetwork.com/sectors/finance',
    provider: {
      '@type': 'Organization',
      name: 'The CISO Network',
    },
    areaServed: {
      '@type': 'DefinedTerm',
      name: 'Financial Services',
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(sectorSchema)}</script>
      <PageHero
        title="Fractional CISO for Financial Services"
        subtitle="Expert cybersecurity leadership for banks, fintechs, and financial institutions"
        eyebrow="Financial Services"
        backgroundImage={getSectorCard('finance')?.image}
        backgroundAlt={getSectorCard('finance')?.imageAlt}
        seoTitle="Fractional CISO Financial Services | The CISO Network"
        seoDescription="Expert fractional CISO services for financial services. Navigate FCA regulations, protect customer data, and prevent costly breaches."
      />

      {/* Why Financial Services Needs CISOs */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-6">
                Cybersecurity Leadership for Financial Services
              </h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  Financial services firms face one of the most challenging cybersecurity landscapes
                  of any sector. As custodians of sensitive financial data and guardians of critical
                  payment infrastructure, banks, fintechs, insurers, and investment firms are prime
                  targets for sophisticated cybercriminals. The sector experiences 300x more cyber
                  attacks than other industries, with attacks growing in both frequency and
                  sophistication.
                </p>
                <p>
                  Regulatory pressure is relentless. The FCA's{' '}
                  <Link to="/services/cyber-resilience" className="text-secondary hover:underline">
                    operational resilience
                  </Link>{' '}
                  requirements, PCI-DSS compliance for card payments, and the incoming DORA (Digital
                  Operational Resilience Act) create a complex web of obligations. Firms must
                  demonstrate robust security controls,{' '}
                  <Link to="/services/incident-response" className="text-secondary hover:underline">
                    incident response
                  </Link>{' '}
                  capabilities, and{' '}
                  <Link to="/services/board-reporting" className="text-secondary hover:underline">
                    board-level accountability
                  </Link>
                  - or face substantial fines and regulatory sanctions.
                </p>
                <p>
                  Yet many financial firms, particularly growing fintechs and smaller institutions,
                  struggle to access the senior cybersecurity expertise they need. A full-time CISO
                  can cost £180,000+ in London, plus bonuses and equity - an investment that
                  early-stage or resource-constrained firms simply cannot justify. This gap leaves
                  them exposed to threats they are ill-equipped to handle.
                </p>
              </div>
            </div>
            <div className="space-y-6">
              <div className="p-6 rounded-xl bg-background border-l-4 border-teal">
                <div className="flex items-start gap-4">
                  <Building2 className="h-6 w-6 text-teal flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-heading font-semibold text-foreground mb-2">
                      FCA & Regulatory Expertise
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      Navigate complex financial regulations with confidence. Our CISOs understand
                      FCA requirements, DORA compliance, and industry-specific frameworks.
                    </p>
                  </div>
                </div>
              </div>
              <div className="p-6 rounded-xl bg-background border-l-4 border-teal">
                <div className="flex items-start gap-4">
                  <Lock className="h-6 w-6 text-teal flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-heading font-semibold text-foreground mb-2">
                      Fintech-Specialist CISOs
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      Access CISOs who understand the unique challenges of scaling fintechs -
                      balancing rapid growth with security, compliance, and customer trust.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Historical Security Cases */}
      <section className="py-24 bg-background">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              When Financial Security Fails
            </h2>
            <p className="text-muted-foreground max-w-3xl mx-auto">
              Historical cases where financial firms suffered breaches that a CISO could have
              prevented
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: AlertTriangle,
                title: 'Capital One (2019)',
                impact: '$190M fine, 100M customers affected',
                description:
                  'A misconfigured web application firewall allowed an attacker to access customer data. A CISO-led security programme would have included regular cloud configuration reviews, least-privilege access controls, and continuous monitoring of cloud environments - preventing this entirely preventable breach.',
              },
              {
                icon: AlertTriangle,
                title: 'Equifax (2017)',
                impact: '$700M settlement, 147M records exposed',
                description:
                  "Failure to patch a known vulnerability in Apache Struts led to one of history's largest data breaches. A CISO would have established robust vulnerability management, ensured timely patching processes, and implemented network segmentation to contain any breach.",
              },
              {
                icon: AlertTriangle,
                title: 'Tesco Bank (2016)',
                impact: '£16.4M FCA fine, £2.26M stolen',
                description:
                  "Cyber criminals exploited deficiencies in Tesco Bank's debit card design and financial crime controls. The FCA found the bank's security measures inadequate. A CISO would have implemented proper card security architecture and real-time fraud detection systems.",
              },
              {
                icon: AlertTriangle,
                title: 'Clydesdale Bank (2013)',
                impact: '£8.9M fine, 42,500 mortgage customers affected',
                description:
                  'A system error meant mortgage customers underpaid interest, with the bank failing to properly notify affected customers. The FCA found serious failings in IT governance. A CISO would establish robust IT risk management and ensure proper customer communication during incidents.',
              },
              {
                icon: AlertTriangle,
                title: 'Metro Bank (2019)',
                impact: 'SS7 attack on SMS 2FA, accounts drained',
                description:
                  'Attackers exploited vulnerabilities in the SS7 signalling protocol to intercept SMS two-factor authentication codes and drain customer accounts. A CISO would have recognised the risks of SMS-based 2FA and implemented stronger authentication methods like app-based MFA.',
              },
              {
                icon: AlertTriangle,
                title: 'NatWest/RBS Outage (2012)',
                impact: '£56M total fines, 6.5M customers affected',
                description:
                  'A failed software upgrade left millions unable to access accounts for weeks. The FCA and PRA issued their first joint enforcement action, fining the banks £42M and £14M respectively. A CISO would have established rigorous IT change management, testing, and rollback procedures.',
              },
            ].map((case_, i) => (
              <div key={i} className="bg-white p-6 rounded-xl shadow-sm border border-border">
                <div className="h-12 w-12 rounded-xl bg-red-100 flex items-center justify-center mb-4">
                  <case_.icon className="h-6 w-6 text-red-600" />
                </div>
                <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
                  {case_.title}
                </h3>
                <p className="text-sm font-medium text-red-600 mb-3">{case_.impact}</p>
                <p className="text-muted-foreground text-sm">{case_.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Risks */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              Critical Risks Facing Financial Firms
            </h2>
            <p className="text-muted-foreground max-w-3xl mx-auto">
              The threats that keep financial security leaders awake at night
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: Shield,
                title: 'Ransomware & Extortion',
                description:
                  'Financial firms are prime targets for ransomware gangs who know downtime costs millions and reputational damage can be catastrophic.',
              },
              {
                icon: TrendingUp,
                title: 'Supply Chain Attacks',
                description:
                  'Third-party fintech integrations and vendor relationships create attack paths that bypass your perimeter security.',
              },
              {
                icon: Lock,
                title: 'Insider Threats',
                description:
                  'Employees with access to sensitive financial data can cause devastating breaches, whether malicious or accidental.',
              },
              {
                icon: Building2,
                title: 'Regulatory Breaches',
                description:
                  'FCA enforcement is increasingly aggressive, with fines reflecting not just breaches but inadequate security governance.',
              },
            ].map((risk, i) => (
              <div key={i} className="text-center">
                <div className="h-14 w-14 mx-auto rounded-xl bg-teal flex items-center justify-center mb-5">
                  <risk.icon className="h-7 w-7 text-accent-foreground" />
                </div>
                <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
                  {risk.title}
                </h3>
                <p className="text-muted-foreground text-sm">{risk.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <PageFAQ
        title="Fractional CISO for Financial Services - FAQs"
        subtitle="Common questions from banks, fintechs, and financial institutions"
        items={[
          {
            id: 'finance-1',
            question: 'How does a fractional CISO work with financial services firms?',
            answer:
              'Our CISOs work with banks, fintechs, insurers, and investment firms on a part-time basis. They can be on-site for board meetings and regulatory discussions, or work remotely for day-to-day programme management. We match you with a CISO who understands FCA requirements and financial sector risk.',
          },
          {
            id: 'finance-2',
            question: 'What regulations should a financial services CISO manage?',
            answer:
              'Financial services CISOs must navigate FCA operational resilience requirements, PCI-DSS for card payments, the incoming DORA regulation, and GDPR. Our CISOs have direct experience implementing controls that satisfy UK and EU financial regulators.',
          },
          {
            id: 'finance-3',
            question: 'How quickly can a fractional CISO start with a fintech?',
            answer:
              'We can typically match you with a financial services CISO within 2-3 weeks. For fintechs approaching FCA authorisation or investment rounds, we can expedite the process. The CISO begins with a rapid assessment of your regulatory posture and security gaps.',
          },
          {
            id: 'finance-4',
            question: 'What is the first thing a fractional CISO does for a financial firm?',
            answer:
              'The first priority is understanding your regulatory perimeter and current compliance posture. Our CISOs review your operational resilience framework, assess incident response capabilities, and identify gaps that could attract regulatory attention or concern from investors.',
          },
          {
            id: 'finance-5',
            question: 'Can a fractional CISO help with FCA authorisation?',
            answer:
              'Yes. FCA authorisation requires evidence of robust security and operational resilience. Our CISOs have helped fintechs prepare their documentation, build compliant security programmes, and satisfy FCA scrutiny of cyber risk management arrangements.',
          },
        ]}
      />

      {/* CTA */}
      <section className="py-24 gradient-navy cta-photo">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-primary-foreground mb-6">
            Secure Your Financial Institution
          </h2>
          <p className="text-primary-foreground/60 text-lg max-w-3xl mx-auto mb-10">
            Don't become another cautionary tale. Our fractional CISOs bring financial services
            expertise to protect your firm, your customers, and your reputation.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="btn-hero">
              Book a Call
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
